import { customerJobDetailPath, customerRequestThreadPath } from '@/routes/customer-portal-routes';

/**
 * Customer Portal — where a customer notification takes them when clicked.
 *
 * The staff resolver's counterpart, kept separate because the two live in
 * different application branches with different route tables (D-050) and must
 * not be able to produce each other's URLs.
 *
 * Both customer notification types are about one job, and the portal has exactly
 * one job destination: `customerJobDetailPath`, the same helper
 * CustomerDashboardPage, CustomerJobListPage and CustomerEquipmentDetailPage
 * already navigate with. It is keyed by the inspection id — `customerJobDetailPath(item.inspectionId)`
 * on the dashboard — which is the id both payloads carry.
 *
 * There is no permission model to consult: the Customer Portal gates on
 * authentication alone (`CustomerProtectedRoute`), and every read is scoped
 * server-side from the customer JWT.
 */
export interface CustomerNotificationDestinationInput {
  type: string;
  payloadJson: unknown;
}

function readId(payloadJson: unknown, key: 'inspectionId' | 'roundId'): string | null {
  if (typeof payloadJson !== 'object' || payloadJson === null || Array.isArray(payloadJson)) {
    return null;
  }
  const id = (payloadJson as Record<string, unknown>)[key];
  return typeof id === 'string' && id.length > 0 ? id : null;
}

export function resolveCustomerNotificationDestination(
  notification: CustomerNotificationDestinationInput,
): string | null {
  switch (notification.type) {
    // ── Stage D-vi (D-145 / OD-D9) — the three modification-round notices ──
    //
    // These are the only customer notifications about a REQUEST rather than a
    // job, and the request now has a page of its own. `modificationPayload`
    // already writes `{ inspectionId, inspectionNumber, roundId, roundNumber }`
    // — ids and numbers only, never the request text — so the round id needed to
    // reach it is present without any change to the worker or the payload.
    //
    // The fallback is not decoration. Rows written before the Request Thread
    // existed carry no `roundId`, and they must keep working: those still open
    // the job, where the request is also visible. So the rule is exactly
    //   roundId present → the request;  absent → the job.
    //
    // Navigation is not authorization. `/customer-portal/requests/:roundId` is
    // guarded by the same customer JWT, tenant, location-scope and permission
    // chain as every other customer read, and a round this contact may not see
    // answers 404 — indistinguishably from one that does not exist. Nothing is
    // checked here, and nothing should be: a link that decided access would be a
    // second authorization system, and a weaker one.
    case 'QUOTATION_REVISION_SENT':
    case 'MODIFICATION_INFORMATION_NEEDED':
    case 'MODIFICATION_ROUND_CLOSED': {
      const roundId = readId(notification.payloadJson, 'roundId');
      if (roundId) return customerRequestThreadPath(roundId);
      const inspectionId = readId(notification.payloadJson, 'inspectionId');
      return inspectionId ? customerJobDetailPath(inspectionId) : null;
    }

    // ── Unchanged by D-vi ─────────────────────────────────────────────────
    // Neither is about a modification round, and neither payload carries one.
    //
    // REPORT_PUBLISHED — the report is downloaded from the job page.
    // `handleReportPublished` writes `{ inspectionId, reportNumber }` and
    // deliberately no revision selector (D-085); the job page resolves the
    // current approved revision server-side, which is exactly why the job is the
    // right destination.
    //
    // INSPECTION_QUOTATION_SENT — mapped for completeness and correctness, but
    // not currently reachable: `handleCommercialSent` writes that row with
    // `customerContactId: null`, and `NotificationService.customerScopeFor`
    // requires an exact contact match, so the customer inbox never returns it.
    // That is a pre-existing, documented trade-off in notification.service.ts (a
    // null-contact customer row cannot be attributed to one customer without
    // leaking it to all of them) and is not changed here. If that row ever gains
    // a contact, this mapping is already correct rather than silently missing.
    case 'REPORT_PUBLISHED':
    case 'INSPECTION_QUOTATION_SENT': {
      const inspectionId = readId(notification.payloadJson, 'inspectionId');
      return inspectionId ? customerJobDetailPath(inspectionId) : null;
    }
    default:
      return null;
  }
}
